import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Adjust path if needed
import { PortableText } from '@portabletext/react';

// Placeholder for loading state
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div> 
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Placeholder for error state
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

const FifthGrade = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('welcome'); // Add tab state

  useEffect(() => {
    const query = `*[_type == "fifthGradePage" && _id == "fifthGradePageContent"][0]{
      pageTitle,
      bannerTitle,
      bannerImage {
        asset->{
          _id,
          url
        }
      },
      tabs[]{
        _key,
        tabTitle,
        tabIdentifier,
        contentSections[]{
          _key,
          title,
          body
        }
      },
      newsletterTitle,
      newsletterSubtitle,
      teacherName,
      newsletterContent
    }`;

    setLoading(true);
    client.fetch(query)
      .then((data) => {
        if (data) {
          setPageData(data);
        } else {
          setError('Fifth Grade Page content not found.');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch fifth grade page data:', err);
        setError('Failed to load page content. Please try again later.');
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Fifth Grade Banner'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
           ) : (
             <div className="absolute inset-0 w-full h-full bg-gray-300"></div> // Placeholder background
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Fifth Grade'}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-6xl mx-auto">
            
            {/* Tabs */}
            <div className="flex overflow-x-auto pb-2 mb-8">
              <button
                className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                  activeTab === 'welcome'
                    ? 'bg-[#691e31] text-white shadow-md'
                    : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                }`}
                onClick={() => setActiveTab('welcome')}
              >
                Welcome to Fifth Grade
              </button>
              <button
                className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                  activeTab === 'newsletter'
                    ? 'bg-[#691e31] text-white shadow-md'
                    : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                }`}
                onClick={() => setActiveTab('newsletter')}
              >
                Monthly Newsletter
              </button>
            </div>

            {/* Welcome Tab Content */}
            {activeTab === 'welcome' && (
              <div className="space-y-6">
                {pageData.tabs && pageData.tabs.length > 0 && pageData.tabs[0].contentSections ? (
                  pageData.tabs[0].contentSections.map((section) => (
                    <section key={section._key} className="bg-[#2a4865]/5 p-5 rounded-lg border-l-4 border-[#2a4865]">
                      <h3 className="text-xl font-bold text-[#2a4865] mb-3 relative after:content-[''] after:absolute after:w-10 after:h-0.5 after:bg-yellow-500 after:-bottom-1 after:left-0">
                        {section.title}
                      </h3>
                      <div className="prose prose-sm sm:prose max-w-none text-gray-700">
                        <PortableText value={section.body} />
                      </div>
                    </section>
                  ))
                ) : (
                  <p>No content sections available.</p>
                )}
              </div>
            )}

            {/* Newsletter Tab Content */}
            {activeTab === 'newsletter' && (
              <div className="space-y-6">
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="bg-[#2a4865] text-white p-6 rounded-t-lg">
                    <h3 className="text-2xl font-bold mb-2 text-white">
                      {pageData.newsletterSubtitle || 'January 2025 Newsletter'}
                    </h3>
                    <p className="text-white/80 text-sm">
                      Fifth Grade Class • {pageData.teacherName || 'Mr. Rodriguez'}
                    </p>
                  </div>
                  
                  <div className="p-6 prose prose-lg max-w-none">
                    {pageData.newsletterContent ? (
                      <PortableText value={pageData.newsletterContent} />
                    ) : (
                      <div>
                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Welcome Back, Fifth Grade Families! 🎓</h2>
                        
                        <p className="text-gray-700 mb-6">
                          Happy New Year! We're excited to dive into the second half of our final elementary year together. 
                          Your fifth graders are developing into independent learners and preparing for middle school!
                        </p>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">What We're Learning This Month 📚</h2>
                        
                        <p className="text-gray-700 mb-3">
                          <strong>Language Arts:</strong> We're diving deep into persuasive writing and analyzing complex texts. 
                          Students are practicing debate skills and learning to support their arguments with evidence.
                        </p>
                        
                        <p className="text-gray-700 mb-3">
                          <strong>Math:</strong> We're exploring fractions, decimals, and percentages with real-world applications. 
                          Students are also beginning work with coordinate geometry and data analysis.
                        </p>
                        
                        <p className="text-gray-700 mb-6">
                          <strong>Science & Social Studies:</strong> Our focus is on the Revolutionary War period and scientific method. 
                          Students are conducting experiments and learning about cause and effect in history.
                        </p>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Important Dates 📅</h2>
                        
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                          <li><strong>January 15:</strong> MLK Day - No School</li>
                          <li><strong>January 20:</strong> Science Fair Project Presentations</li>
                          <li><strong>January 27:</strong> Fifth Grade Student Council Elections</li>
                          <li><strong>February 3:</strong> Middle School Transition Meeting for Parents</li>
                        </ul>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Middle School Preparation 🎒</h2>
                        
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                          <li>Practice time management with daily planners</li>
                          <li>Encourage independent homework completion</li>
                          <li>Discuss organization strategies for multiple subjects</li>
                          <li>Build confidence in asking for help when needed</li>
                        </ul>

                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                          <h3 className="font-bold text-[#691e31] mb-2">📧 Questions or Concerns?</h3>
                          <p className="text-gray-700 text-sm">
                            As we prepare for middle school, please don't hesitate to reach out with any questions 
                            about your child's academic or social development.
                          </p>
                          <p className="text-gray-700 text-sm mt-2">
                            <strong>Let's make this final elementary year amazing!</strong><br/>
                            <em>- {pageData.teacherName || 'Mr. Rodriguez'}, Fifth Grade Teacher</em>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FifthGrade; 